import React, { useCallback, useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faBroom, faArrowRight } from "@fortawesome/free-solid-svg-icons"

import { useCleanOffPath } from "hooks/api/useCleanOffPath"
import { BaseCheckCleanStaleError } from "api/Repos/data-checks"
import { HashChip } from "shared/ui/HashChip/HashChip.component"
import ProgressModal from "shared/ui/ProgressModal/ProgressModal.component"
import { provenanceLink } from "features/Provenance/utils/provenanceLinks"
import CleanConfirmModal from "features/DataChecks/components/CleanConfirmModal.component"
import type {
  BaseCheckCleanResult,
  BaseCheckDiagnostic,
  BaseCheckQuestion,
  BaseCheckResult,
} from "types/dataChecks.types"

interface CleanPanelProps {
  projectId: string
  owner: string
  /** The report the Base check section produced — the same cache entry, not a copy. */
  report?: BaseCheckResult
  /** True while the report is (re-)running; the apply is held until it settles. */
  isReportFetching: boolean
  /** Re-runs the check. Called after a clean (the new baseline) and after a stale refusal. */
  refetchReport: () => void
  /** Switches the tab's section back to the base check, for the "there is no report yet" path. */
  onGoToBaseCheck: () => void
}

/** One question this clean will not touch, and the sentence that says why. */
export interface CleanExclusion {
  question: BaseCheckQuestion
  reason: string
}

export interface CleanScopePreview {
  /** Questions the clean will act on. */
  cleanable: BaseCheckQuestion[]
  /** Questions carrying off-path answers that it will NOT act on, each with its reason. */
  excluded: CleanExclusion[]
  /** Off-path ANSWERS on the cleanable set — respondents per question instance, summed. */
  offPathAnswers: number
  /** Distinct variables the report MEASURED as carrying off-path answers on that set. */
  variableCount: number
}

/**
 * What a clean of this report would act on, derived from the report alone.
 *
 * THE SAME RULE THE SERVER APPLIES, deliberately: `cleanable`, over the questions that carry an
 * off-path answer. Coverage plays no part: it says whether tab specs can see the asked
 * condition, while the clean blanks the cells the walker found off path, which it found for
 * every question. The rule is stated twice — here and in `CleanBaseCheck` — because the server
 * must never be talked into cleaning something by a client, and the client must never show a
 * promise the server will not keep. Where the two could still disagree (a report read from a
 * stale cache, or a column two questions read) the server is the one that decides, and it
 * explains itself: every question or column it declined to clean comes back as a diagnostic,
 * and this panel renders them verbatim.
 *
 * `variableCount` counts only fields the report could actually count (`countable`). It is a
 * floor: the clean works in data columns, and one category field is several of them.
 */
export const previewCleanScope = (report: BaseCheckResult): CleanScopePreview => {
  const carriesOffPath = report.questions
    .filter((question) => question.offPath > 0)
    .sort((a, b) => a.ordinal - b.ordinal)
  const cleanable: BaseCheckQuestion[] = []
  const excluded: CleanExclusion[] = []

  for (const question of carriesOffPath) {
    if (!question.cleanable) {
      excluded.push({
        question,
        reason:
          question.kind === "data"
            ? "a data variable — never offered to cleaning"
            : question.kind === "hidden"
              ? "a hidden question — reported, but not offered to cleaning in this version"
              : "not offered to cleaning",
      })
      continue
    }
    cleanable.push(question)
  }

  const variables = new Set<string>()
  let offPathAnswers = 0
  for (const question of cleanable) {
    offPathAnswers += question.offPath
    for (const instance of question.instances) {
      for (const field of instance.fields) {
        if (field.countable && field.offPath > 0) variables.add(field.path)
      }
    }
  }

  return {
    cleanable,
    excluded,
    offPathAnswers,
    variableCount: variables.size,
  }
}

/** The history entry's failure, in the user's terms. The data is cleaned either way. */
const AUDIT_REASON: Record<string, string> = {
  repos_unavailable: "the project store could not be reached",
  repos_error: "the project store refused the entry",
  busy: "another change was being recorded at the same moment",
}

/**
 * A saved clean whose history entry failed. Short on purpose: the data IS cleaned, and the one
 * thing the user needs is that the Provenance timeline will not show it.
 */
const AuditWarning: React.FC<{ result: BaseCheckCleanResult }> = ({ result }) => {
  if (result.auditStatus !== "not_recorded") return null
  const why = result.reason ? AUDIT_REASON[result.reason] : undefined
  return (
    <div
      role="alert"
      className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-[12px] leading-snug text-amber-800"
    >
      The data was cleaned, but its history entry could not be saved
      {why ? ` (${why})` : ""}, so the Provenance timeline will not show this clean.
    </div>
  )
}

/** The server's own explanations, rendered verbatim. */
const Diagnostics: React.FC<{ diagnostics: BaseCheckDiagnostic[] }> = ({ diagnostics }) => {
  if (diagnostics.length === 0) return null
  return (
    <div className="mt-2 max-h-60 overflow-y-auto rounded border border-slate-200 bg-white">
      {diagnostics.map((diagnostic, index) => (
        <div
          key={`${diagnostic.code}-${diagnostic.question}-${index}`}
          className="border-b border-slate-100 px-2 py-1.5 text-[12px] text-slate-600 last:border-b-0"
        >
          <span className="font-mono text-[11px] text-slate-400">{diagnostic.code}</span>
          {/* A diagnostic's question may be EMPTY — it is then about the request as a whole, and
              renders without a question anchor rather than being attached to a nearby one. */}
          {diagnostic.question && (
            <span className="ml-1 text-[11px] text-slate-500">· {diagnostic.question}</span>
          )}
          <div className="break-words">{diagnostic.message}</div>
        </div>
      ))}
    </div>
  )
}

const QuestionList: React.FC<{ questions: BaseCheckQuestion[] }> = ({ questions }) => (
  <div className="max-h-56 overflow-y-auto rounded border border-slate-200 bg-white">
    {questions.map((question) => (
      <div
        key={`${question.ordinal}-${question.external}`}
        className="flex flex-wrap items-baseline gap-x-2 border-b border-slate-100 px-2 py-1.5 last:border-b-0"
      >
        <span className="text-[13px] text-slate-800">
          {question.label || question.external}
        </span>
        <span className="font-mono text-[11px] text-slate-400">{question.external}</span>
        <span className="text-[12px] font-medium text-rose-700">
          {question.offPath.toLocaleString()} off path
        </span>
      </div>
    ))}
  </div>
)

const Stat: React.FC<{ label: string; value: string; hint: string }> = ({
  label,
  value,
  hint,
}) => (
  <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-slate-600">
    <div className="text-[11px] font-semibold uppercase tracking-wide opacity-70">{label}</div>
    <div className="mt-0.5 text-2xl font-semibold leading-none">{value}</div>
    <div className="mt-1 text-[11px] leading-snug opacity-80">{hint}</div>
  </div>
)

/**
 * The Cleaning section: apply a base-check report to the data.
 *
 * ONE REPORT, NOT TWO. This panel does not run its own check. It is handed the very report the
 * Base check section is showing (one react-query entry, two observers — see the container), and
 * a clean is only ever an approval of THAT report: its `variant` chooses the flow the server
 * walks and its `dataHash` is the consent the server re-checks before it writes. Applying
 * something the user has not read is the failure mode this whole design exists to prevent, so
 * "no report" is a disabled button with a sentence, never a convenient auto-run.
 *
 * WHAT THE SCREEN OWES THE USER, and where each part comes from:
 *  - the set that will be acted on, and the set that will NOT be, with reasons — computed from
 *    the report by {@link previewCleanScope} before anything is sent;
 *  - what the operation does to the project, at the moment of consent — the confirm modal;
 *  - what it actually did, in the units it measured — the result block, from the server's own
 *    `clean` summary, never re-derived here;
 *  - why it did not do something — the server's diagnostics, rendered verbatim on every path,
 *    including the successful one (a clean can succeed and still have REFUSED a column two
 *    questions both claim, and that refusal is exactly what a user needs to be told).
 *
 * THE THREE OUTCOMES ARE NOT THREE FAILURES. A clean that changed nothing is an ordinary
 * answer with an explanation attached; a stale refusal is the data having moved, not an error;
 * only a `*_failed` status is a failure, and its `error` says what was written (at most the
 * pre-clean copy, which it then names). The tone of each
 * block is chosen accordingly.
 *
 * @component
 */
const CleanPanel: React.FC<CleanPanelProps> = ({
  projectId,
  owner,
  report,
  isReportFetching,
  refetchReport,
  onGoToBaseCheck,
}) => {
  const [confirming, setConfirming] = useState(false)
  // The stale refusal, kept on screen. The toast that announces it is deliberately transient and
  // this is not: the user is about to be looking at a re-run report and needs to know why.
  const [stale, setStale] = useState<BaseCheckCleanStaleError | null>(null)
  const navigate = useNavigate()
  const clean = useCleanOffPath()

  const preview = useMemo(
    () => (report && report.status === "ok" ? previewCleanScope(report) : null),
    [report]
  )

  // Why the apply is not available, in the user's terms. First match wins, and the order is the
  // order the reasons actually apply in.
  const blocked = !report
    ? "Run the base check first — a clean applies a report you have read."
    : report.status !== "ok"
      ? "The check did not run on this project, so nothing was measured and there is nothing to apply."
      : !report.dataHash
        ? "This report carries no data stamp, so there is nothing to confirm the clean against."
        : !preview || preview.cleanable.length === 0
          ? "Nothing to clean: no question offered to cleaning on this report carries an off-path answer."
          : null

  const apply = useCallback((): void => {
    setConfirming(false)
    // Defensive: the modal that calls this is only rendered when both are present, and the api
    // layer throws on an empty hash. Kept because the alternative is an unguarded apply.
    if (!report || !report.dataHash) return
    setStale(null)

    void clean
      .mutateAsync({
        projectId,
        owner,
        request: {
          // v1 applies the whole eligible set. A per-question scope is a documented server
          // capability and a deliberate non-goal here: a partial clean is a second decision to
          // explain, and the report is read as a whole.
          scope: "all",
          // The ENGINE'S echo, not a requested variant — the clean must walk the flow that was
          // measured, and an empty request variant means "the questionnaire's own first".
          variant: report.variant,
          confirmDataHash: report.dataHash,
          // The routing the user read: a questionnaire saved since the report is a 409, so the
          // clean never blanks by a routing nobody saw.
          ...(report.qnrHash ? { confirmQnrHash: report.qnrHash } : {}),
        },
      })
      .then((result) => {
        // Only a clean that WROTE invalidates the baseline. A no-op left the file alone, so the
        // report on screen still describes it — re-running the check would cost minutes and
        // change nothing.
        if (result.changed) refetchReport()
      })
      .catch((error: unknown) => {
        if (error instanceof BaseCheckCleanStaleError) {
          setStale(error)
          refetchReport()
        }
        // Everything else has already been notified by the mutation's onError.
      })
  }, [clean, owner, projectId, refetchReport, report])

  const result = clean.data
  const summary = result?.changed ? result.clean : undefined

  return (
    <div className="mx-auto max-w-6xl p-6">
      <ProgressModal
        title="Cleaning the data"
        isLoading={clean.isPending}
        duration={0}
        indeterminate
        subtitle="Re-running the check, storing a copy of the data as it is now, blanking the off-path answers, then writing the cleaned file back. The data is written only if every step before it succeeds."
      />

      <div className="mb-4 flex items-start justify-between gap-4">
        <div>
          <h3 className="m-0 text-[15px] font-semibold text-slate-800">Cleaning</h3>
          <p className="m-0 mt-0.5 text-[12px] text-slate-500">
            Blank the answers the routing says were never asked — the off-path set of the report
            you have just read, and nothing else.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setConfirming(true)}
          disabled={blocked !== null || clean.isPending || isReportFetching}
          className="dq-btn dq-btn--primary shrink-0"
        >
          <FontAwesomeIcon icon={faBroom} />
          Clean off-path answers
        </button>
      </div>

      {blocked && (
        <div className="mb-4 rounded-lg border border-dashed border-slate-200 p-4 text-[13px] text-slate-500">
          <p className="m-0">{blocked}</p>
          {!report && (
            <button
              type="button"
              onClick={onGoToBaseCheck}
              className="mt-2 inline-flex items-center gap-1.5 text-[12px] font-medium text-accent hover:underline"
            >
              Go to the base check <FontAwesomeIcon icon={faArrowRight} className="h-3 w-3" />
            </button>
          )}
        </div>
      )}

      {/* The Base check section is not mounted while this one is, so its own progress modal is
          not on screen — and a clean is followed by a re-run the user did not press a button
          for. Say that work is happening rather than leaving a disabled button unexplained. */}
      {isReportFetching && (
        <div className="mb-4 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-[12px] text-slate-600">
          Re-running the base check against the data as it now stands — this takes a moment on a
          large file.
        </div>
      )}

      {stale !== null && (
        <div className="mb-4 rounded-lg border border-sky-200 bg-sky-50 p-3 text-[12px] text-sky-800">
          <p className="m-0 font-medium">
            The data or the questionnaire changed since that report, so nothing was cleaned.
          </p>
          {/* The server's sentence says which moved, and names the pre-clean copy when one was
              already stored before the change was found. */}
          {stale.message && <p className="m-0 mt-1 leading-snug">{stale.message}</p>}
          <p className="m-0 mt-1 leading-snug">
            The report you approved no longer describes this project. The check has been re-run;
            read it again and apply from the new one.
          </p>
          {(stale.dataHash || stale.qnrHash) && (
            <div className="mt-1.5 flex items-center gap-2">
              <span>The project now stamps as</span>
              {stale.dataHash && <HashChip label="data" hash={stale.dataHash} length={12} />}
              {stale.qnrHash && <HashChip label="qnr" hash={stale.qnrHash} length={12} />}
            </div>
          )}
        </div>
      )}

      {report && report.status === "ok" && preview && (
        <>
          <div className="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
            <Stat
              label="Questions to clean"
              value={preview.cleanable.length.toLocaleString()}
              hint="offered to cleaning and carrying an off-path answer"
            />
            <Stat
              label="Off-path answers"
              value={preview.offPathAnswers.toLocaleString()}
              hint={
                report.totals.offPath === preview.offPathAnswers
                  ? "every off-path answer on this report is in scope"
                  : `of ${report.totals.offPath.toLocaleString()} on the report — the rest are on questions this clean will not touch`
              }
            />
            <Stat
              label="Variables"
              value={preview.variableCount.toLocaleString()}
              // DISTINCT data columns, each counted once however many question instances carry
              // it — deliberately not the report's "variable occurrences" figure, which counts
              // one per instance. Two adjacent screens, two figures, so each names its unit.
              hint="distinct data columns measured as carrying off-path answers, counted once each — a category question is several of them"
            />
            <Stat
              label="Undetermined"
              value={report.totals.undetermined.toLocaleString()}
              // Not "never touched": the same respondent can be undetermined on one cell and off
              // path on another, and that other cell IS cleaned. What is never touched is a cell
              // whose route is unknown.
              hint="a gating answer is blank or can't be read — cells whose route is unknown are never cleaned"
            />
          </div>

          {preview.cleanable.length > 0 && (
            <div className="mb-4">
              <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                What will be cleaned
              </div>
              <QuestionList questions={preview.cleanable} />
            </div>
          )}

          {preview.excluded.length > 0 && (
            <div className="mb-4">
              <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                Carries off-path answers, and will NOT be cleaned
              </div>
              <div className="rounded border border-slate-200 bg-white">
                {preview.excluded.map(({ question, reason }) => (
                  <div
                    key={`${question.ordinal}-${question.external}`}
                    className="border-b border-slate-100 px-2 py-1.5 text-[12px] text-slate-600 last:border-b-0"
                  >
                    <span className="text-[13px] text-slate-800">
                      {question.label || question.external}
                    </span>
                    <span className="ml-2 font-mono text-[11px] text-slate-400">
                      {question.external}
                    </span>
                    <span className="ml-2 text-[12px] text-rose-700">
                      {question.offPath.toLocaleString()} off path
                    </span>
                    <div className="leading-snug">{reason}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="mb-4 rounded-lg border border-slate-200 p-3 text-[12px] leading-snug text-slate-600">
            <p className="m-0">
              Applying this rewrites the project&apos;s working data file. A copy of the data as
              it is now is stored first as a project document, so the data as it was can be
              recovered from Documents; your raw import files are untouched either way.
              Cells whose route is undetermined are never cleaned.
            </p>
            {report.dataHash && (
              <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-slate-500">
                <span>This report describes the data stamped</span>
                <HashChip label="data" hash={report.dataHash} length={12} />
                <span>
                  flow{" "}
                  <span className="font-mono text-slate-700">
                    {report.variant || "(none reported)"}
                  </span>
                </span>
              </div>
            )}
          </div>
        </>
      )}

      {/* ---- what the last apply did -------------------------------------------------- */}

      {summary && result && <AuditWarning result={result} />}

      {summary && (
        <div className="mb-4 rounded-lg border border-emerald-200 bg-emerald-50 p-3">
          <p className="m-0 text-[13px] font-medium text-emerald-800">
            {summary.cellsBlanked.toLocaleString()} cell
            {summary.cellsBlanked === 1 ? "" : "s"} blanked across{" "}
            {summary.variablesBlanked.toLocaleString()} variable
            {summary.variablesBlanked === 1 ? "" : "s"} on{" "}
            {summary.questionsCleaned.toLocaleString()} question
            {summary.questionsCleaned === 1 ? "" : "s"}.
            {/* OMITTED, never zeroed — an absent figure is not "nobody was affected". */}
            {summary.respondentsAffected !== undefined &&
              ` ${summary.respondentsAffected.toLocaleString()} respondent${
                summary.respondentsAffected === 1 ? "" : "s"
              } affected.`}
          </p>
          <p className="m-0 mt-1 text-[12px] leading-snug text-emerald-800">
            The data as it was is stored as{" "}
            <span className="break-all font-mono text-[11px]">{summary.precleanDocument}</span> —
            you will find it with the project&apos;s documents. Cells are not answers: one
            off-path answer on a five-option question is five blanked cells.
          </p>
          <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-emerald-700">
            <span>before</span>
            <HashChip label="data" hash={summary.preDataHash} length={12} />
            <span>after</span>
            <HashChip label="data" hash={summary.postDataHash} length={12} />
            <button
              type="button"
              // The section matters: the clean's evidence is the `data.cleaned` record in the
              // chain, and landing on whichever Provenance section was last open would make the
              // user hunt for it.
              onClick={() => navigate(provenanceLink(projectId, "timeline"))}
              className="inline-flex items-center gap-1.5 font-medium text-emerald-800 hover:underline"
            >
              See it in the Provenance timeline{" "}
              <FontAwesomeIcon icon={faArrowRight} className="h-3 w-3" />
            </button>
          </div>
          <Diagnostics diagnostics={result?.diagnostics ?? []} />
        </div>
      )}

      {result && !result.changed && result.status === "ok" && (
        <div className="mb-4 rounded-lg border border-slate-200 bg-slate-50 p-3">
          <p className="m-0 text-[13px] font-medium text-slate-700">
            Nothing was cleaned, and nothing on this project was changed.
          </p>
          <p className="m-0 mt-1 text-[12px] leading-snug text-slate-600">
            The clean ran and found nothing it was entitled to blank. That is an answer, not a
            failure — here is what it says about each question it left alone.
          </p>
          <Diagnostics diagnostics={result.diagnostics} />
        </div>
      )}

      {result && !result.changed && result.status !== "ok" && (
        <div className="mb-4 rounded-lg border border-rose-200 bg-rose-50 p-3">
          <p className="m-0 text-[13px] font-medium text-rose-800">
            The clean did not complete.
          </p>
          {result.error && (
            <p className="m-0 mt-1 break-words text-[12px] text-rose-700">{result.error}</p>
          )}
          <p className="m-0 mt-1 text-[12px] leading-snug text-rose-700">
            The message above says what happened to the project&apos;s data file. A clean saves the
            cleaned data only after a copy of the data as it was is stored, and the message names
            that copy when there is one. The report above still stands; you can try again.
          </p>
          {result.log.length > 0 && (
            <details className="mt-2">
              <summary className="cursor-pointer text-[11px] text-rose-700">
                Engine trace ({result.log.length} lines)
              </summary>
              <pre className="m-0 mt-1 max-h-60 overflow-auto whitespace-pre-wrap break-all rounded bg-white p-2 font-mono text-[11px] text-slate-600">
                {result.log.join("\n")}
              </pre>
            </details>
          )}
          <Diagnostics diagnostics={result.diagnostics} />
        </div>
      )}

      {/* Rendered once, below all three result blocks, because "you named something that is not
          here" is true of a clean that wrote, one that did nothing and one that failed alike —
          the server puts it in the request block of every answer for that reason. Unreachable
          while the UI sends `scope: "all"`; live the day a per-question scope ships. */}
      {result && result.scopeUnmatched.length > 0 && (
        <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-[12px] leading-snug text-amber-800">
          Named in the request but not found on this questionnaire:{" "}
          {result.scopeUnmatched.join(", ")}. Those names matched no question, so nothing was
          done about them.
        </div>
      )}

      {report && report.status === "ok" && preview && report.dataHash && (
        <CleanConfirmModal
          open={confirming}
          questionCount={preview.cleanable.length}
          offPathAnswers={preview.offPathAnswers}
          variableCount={preview.variableCount}
          variant={report.variant}
          dataHash={report.dataHash}
          qnrHash={report.qnrHash || undefined}
          onCancel={() => setConfirming(false)}
          onConfirm={apply}
        />
      )}
    </div>
  )
}

export default CleanPanel
